import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type JSX, type ReactNode } from 'react';

import { Select } from '@/components/ui/Select';
import { ApiError } from '@/lib/api';
import { checkNit } from '@/lib/tenant-identity';

import {
  getTenantBillingProfile,
  updateTenantBillingProfile,
  type TenantBillingProfile,
  type UpdateTenantBillingProfileInput,
} from '../api/tenants.graphql';
import styles from '../pages/EditTenantPage.module.scss';
import {
  BILLING_CYCLE_OPTIONS,
  BILLING_REQUIREMENT_LABELS,
  formatLifecycleDate,
  optionLabel,
  PAYMENT_METHOD_OPTIONS,
  statusLabel,
  TAX_REGIME_OPTIONS,
} from '../tenant-labels';

export function tenantBillingQueryKey(tenantId: string): readonly unknown[] {
  // Bajo `platform-tenants`: guardar Datos generales (razón social, NIT) también lo recarga.
  return ['platform-tenants', 'billing', tenantId];
}

interface BillingForm {
  legalName: string;
  nit: string;
  taxRegime: string;
  address: string;
  city: string;
  department: string;
  billingEmail: string;
  billingContactName: string;
  billingContactPhone: string;
  billingCycle: string;
  paymentMethodType: string;
  notes: string;
}

type FieldErrors = Partial<Record<keyof BillingForm, string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[\d\s()-]{7,20}$/;

function formFromProfile(p: TenantBillingProfile): BillingForm {
  return {
    legalName: p.legalName ?? '',
    nit: p.nit ? (p.verificationDigit ? `${p.nit}-${p.verificationDigit}` : p.nit) : '',
    taxRegime: p.taxRegime ?? '',
    address: p.address ?? '',
    city: p.city ?? '',
    department: p.department ?? '',
    billingEmail: p.billingEmail ?? '',
    billingContactName: p.billingContactName ?? '',
    billingContactPhone: p.billingContactPhone ?? '',
    billingCycle: p.billingCycle ?? 'monthly',
    paymentMethodType: p.paymentMethodType ?? '',
    notes: p.notes ?? '',
  };
}

/** Valida en la consola lo mismo que el API (que sigue siendo la fuente de verdad). */
export function buildBillingInput(form: BillingForm): {
  input: UpdateTenantBillingProfileInput | null;
  errors: FieldErrors;
} {
  const errors: FieldErrors = {};
  if (!form.legalName.trim()) {errors.legalName = 'La razón social es obligatoria para facturar.';}
  const nit = checkNit(form.nit);
  if (!form.nit.trim()) {errors.nit = 'El NIT es obligatorio para facturar.';}
  else if (!nit.ok) {errors.nit = nit.message;}
  if (!EMAIL.test(form.billingEmail.trim())) {
    errors.billingEmail = 'Escribe un email de facturación válido.';
  }
  if (form.billingContactPhone.trim() && !PHONE.test(form.billingContactPhone.trim())) {
    errors.billingContactPhone = 'Escribe un teléfono válido, p. ej. +57 300 123 4567.';
  }
  if (Object.keys(errors).length > 0 || !nit.ok) {
    return { input: null, errors };
  }
  const optional = (value: string): string | null => value.trim() || null;
  return {
    input: {
      legalName: form.legalName.trim(),
      nit: nit.nit,
      verificationDigit: nit.verificationDigit,
      taxRegime: optional(form.taxRegime),
      address: optional(form.address),
      city: optional(form.city),
      department: optional(form.department),
      billingEmail: form.billingEmail.trim(),
      billingContactName: optional(form.billingContactName),
      billingContactPhone: optional(form.billingContactPhone),
      billingCycle: form.billingCycle,
      paymentMethodType: optional(form.paymentMethodType),
      notes: optional(form.notes),
    },
    errors,
  };
}

interface Props {
  tenantId: string;
  /** `platform.billing.write`; sin ella la pestaña es de solo lectura. */
  canWrite: boolean;
  /** La identidad legal es la misma de Datos generales: se recarga tras guardar. */
  onSaved: () => void;
}

/**
 * OS-1722 — pestaña Facturación de la ficha. La razón social, el NIT, el régimen y la dirección
 * son los del tenant (los mismos de Datos generales); el resto son metadatos de cobro de OpsSynk.
 * No es la facturación DIAN del restaurante ni guarda datos de tarjeta.
 */
export function TenantBillingTab({ tenantId, canWrite, onSaved }: Props): JSX.Element {
  const qc = useQueryClient();
  const idPrefix = useId();
  const queryKey = tenantBillingQueryKey(tenantId);
  const query = useQuery({ queryKey, queryFn: () => getTenantBillingProfile(tenantId) });
  const [form, setForm] = useState<BillingForm | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data && form === null) {
      setForm(formFromProfile(query.data));
    }
  }, [query.data, form]);

  const mutation = useMutation({
    mutationFn: (input: UpdateTenantBillingProfileInput) =>
      updateTenantBillingProfile(tenantId, input),
    onSuccess: (profile) => {
      qc.setQueryData(queryKey, profile);
      setForm(formFromProfile(profile));
      setSaved(true);
      onSaved();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.field) {
        const key = (err.field === 'verificationDigit' ? 'nit' : err.field) as keyof BillingForm;
        setErrors({ [key]: err.message });
        document.getElementById(`${idPrefix}-${key}`)?.focus();
      }
    },
  });

  if (query.isLoading) {return <p className={styles.loading} role="status">Cargando…</p>;}
  if (query.isError) {
    return (
      <p className={styles.error} role="alert">
        No se pudo cargar la facturación: {query.error.message}
      </p>
    );
  }
  const profile = query.data;
  if (!profile || !form) {return <p className={styles.loading} role="status">Cargando…</p>;}

  function update(key: keyof BillingForm, value: string): void {
    setSaved(false);
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setErrors((prev) => {
      if (!(key in prev)) {return prev;}
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!form) {return;}
    const { input, errors: found } = buildBillingInput(form);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(`${idPrefix}-${first}`)?.focus();
      return;
    }
    if (input) {mutation.mutate(input);}
  }

  const fieldProps = (
    key: keyof BillingForm,
  ): { id: string; 'aria-invalid': boolean; 'aria-describedby': string | undefined } => ({
    id: `${idPrefix}-${key}`,
    'aria-invalid': Boolean(errors[key]),
    'aria-describedby': errors[key] ? `${idPrefix}-${key}-error` : undefined,
  });
  const fieldError = (key: keyof BillingForm): ReactNode =>
    errors[key] ? (
      <span id={`${idPrefix}-${key}-error`} className={styles.fieldError} role="alert">
        {errors[key]}
      </span>
    ) : null;

  const status = profile.complete ? (
    <p className={styles.fieldHint}>Perfil completo: el tenant se puede convertir a pago.</p>
  ) : (
    <p className={styles.fieldHint} role="note">
      Para convertir a pago falta:{' '}
      {profile.missing.map((key) => BILLING_REQUIREMENT_LABELS[key] ?? key).join(', ')}.
    </p>
  );
  const context = (
    <p className={styles.fieldHint}>
      Estado: {statusLabel(profile.planStatus)}
      {profile.convertedAt ? ` · convertido a pago el ${formatLifecycleDate(profile.convertedAt)}` : ''}
      {profile.updatedAt ? ` · actualizado el ${formatLifecycleDate(profile.updatedAt)}` : ''}
    </p>
  );

  if (!canWrite) {
    const rows: [string, string][] = [
      ['Razón social', profile.legalName ?? '—'],
      [
        'NIT',
        profile.nit ? `${profile.nit}${profile.verificationDigit ? `-${profile.verificationDigit}` : ''}` : '—',
      ],
      ['Régimen fiscal', optionLabel(TAX_REGIME_OPTIONS, profile.taxRegime)],
      ['Dirección fiscal', [profile.address, profile.city, profile.department].filter(Boolean).join(', ') || '—'],
      ['Email de facturación', profile.billingEmail ?? '—'],
      ['Contacto de facturación', profile.billingContactName ?? '—'],
      ['Teléfono de facturación', profile.billingContactPhone ?? '—'],
      ['Ciclo de facturación', optionLabel(BILLING_CYCLE_OPTIONS, profile.billingCycle)],
      ['Medio de pago', optionLabel(PAYMENT_METHOD_OPTIONS, profile.paymentMethodType)],
      ['Notas', profile.notes ?? '—'],
    ];
    return (
      <section className={styles.panel} aria-labelledby={`${idPrefix}-title`}>
        <h2 id={`${idPrefix}-title`} className={styles.panelTitle}>
          Facturación
        </h2>
        <p className={styles.fieldHint}>Solo lectura: tu rol no edita la facturación.</p>
        {context}
        {status}
        <dl className={styles.grid}>
          {rows.map(([term, value]) => (
            <div key={term} className={styles.field}>
              <dt>{term}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>
    );
  }

  return (
    <form className={styles.panel} onSubmit={handleSubmit} noValidate aria-labelledby={`${idPrefix}-title`}>
      <h2 id={`${idPrefix}-title`} className={styles.panelTitle}>
        Facturación
      </h2>
      {context}
      {status}

      <h3 className={styles.panelTitle}>Identidad fiscal</h3>
      <p className={styles.fieldHint}>
        Son los datos legales del tenant (los mismos de Datos generales): cambiarlos aquí los cambia
        allá. No afecta la facturación electrónica DIAN del restaurante.
      </p>
      <div className={styles.grid}>
        <label className={styles.field}>
          Razón social *
          <input
            type="text"
            {...fieldProps('legalName')}
            value={form.legalName}
            onChange={(e) => update('legalName', e.target.value)}
            placeholder="Mi Restaurante S.A.S."
          />
          {fieldError('legalName')}
        </label>
        <label className={styles.field}>
          NIT (con dígito de verificación) *
          <input
            type="text"
            inputMode="numeric"
            {...fieldProps('nit')}
            value={form.nit}
            onChange={(e) => update('nit', e.target.value)}
            placeholder="900123456-8"
          />
          {fieldError('nit')}
        </label>
        <label className={styles.field}>
          Régimen fiscal
          <Select
            {...fieldProps('taxRegime')}
            value={form.taxRegime}
            onChange={(value) => update('taxRegime', value)}
            options={[{ value: '', label: 'Sin definir' }, ...TAX_REGIME_OPTIONS]}
          />
        </label>
        <label className={styles.field}>
          Dirección fiscal
          <input
            type="text"
            {...fieldProps('address')}
            value={form.address}
            onChange={(e) => update('address', e.target.value)}
            placeholder="Calle 1 # 2-3"
          />
        </label>
        <label className={styles.field}>
          Ciudad
          <input
            type="text"
            {...fieldProps('city')}
            value={form.city}
            onChange={(e) => update('city', e.target.value)}
          />
        </label>
        <label className={styles.field}>
          Departamento
          <input
            type="text"
            {...fieldProps('department')}
            value={form.department}
            onChange={(e) => update('department', e.target.value)}
          />
        </label>
      </div>

      <h3 className={styles.panelTitle}>Cobro</h3>
      <div className={styles.grid}>
        <label className={styles.field}>
          Email de facturación *
          <input
            type="email"
            {...fieldProps('billingEmail')}
            value={form.billingEmail}
            onChange={(e) => update('billingEmail', e.target.value)}
            placeholder="facturas@empresa.com"
          />
          {fieldError('billingEmail')}
        </label>
        <label className={styles.field}>
          Contacto de facturación
          <input
            type="text"
            {...fieldProps('billingContactName')}
            value={form.billingContactName}
            onChange={(e) => update('billingContactName', e.target.value)}
          />
          {fieldError('billingContactName')}
        </label>
        <label className={styles.field}>
          Teléfono de facturación
          <input
            type="tel"
            {...fieldProps('billingContactPhone')}
            value={form.billingContactPhone}
            onChange={(e) => update('billingContactPhone', e.target.value)}
            placeholder="+57 300 123 4567"
          />
          {fieldError('billingContactPhone')}
        </label>
        <label className={styles.field}>
          Ciclo de facturación
          <Select
            {...fieldProps('billingCycle')}
            value={form.billingCycle}
            onChange={(value) => update('billingCycle', value)}
            options={BILLING_CYCLE_OPTIONS}
          />
        </label>
        <label className={styles.field}>
          Medio de pago
          <Select
            {...fieldProps('paymentMethodType')}
            value={form.paymentMethodType}
            onChange={(value) => update('paymentMethodType', value)}
            options={[{ value: '', label: 'Sin definir' }, ...PAYMENT_METHOD_OPTIONS]}
          />
          <span className={styles.fieldHint}>Solo el tipo: nunca se guardan datos de tarjeta.</span>
        </label>
        <label className={styles.field}>
          Notas
          <textarea
            {...fieldProps('notes')}
            value={form.notes}
            onChange={(e) => update('notes', e.target.value)}
            rows={3}
            maxLength={1000}
          />
          {fieldError('notes')}
          <span className={styles.fieldHint}>No escribas números de tarjeta ni contraseñas.</span>
        </label>
      </div>

      {mutation.error && !(mutation.error instanceof ApiError && mutation.error.field) ? (
        <p className={styles.error} role="alert">
          {mutation.error.message}
        </p>
      ) : null}
      <div>
        <button type="submit" className={styles.saveBtn} disabled={mutation.isPending}>
          <Save size={16} aria-hidden="true" />
          {mutation.isPending ? 'Guardando…' : 'Guardar facturación'}
        </button>
      </div>
      <p className={styles.success} role="status">
        {saved ? 'Facturación guardada.' : ''}
      </p>
    </form>
  );
}
